import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, ArrowRight, Building2, CheckCircle2, ClipboardList, History, Lock, RefreshCw, RotateCcw,
  Search, ShieldCheck, Tags, X,
} from "lucide-react";
import { EmptyState, PageHeader } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, fmtDateTime } from "../../lib/api";

/**
 * Phase 231 — Super Admin › Tax Head Re-tag.
 * Choose a municipality, the tax head to move demands FROM (or any head) and the head to move them TO, tick the properties
 * (search + checkboxes + Select all), optionally include self-assessment demands → the page runs a DRY RUN (nothing changes)
 * and says how many demands would move. Only "Approve & apply" (typing the municipality code) changes data: a backup is
 * saved first, then only the tax head (and, for a second line in the same year, its line label) is updated; every run is
 * listed with a one-click Revert. Amounts and payment status are never touched. Nothing runs on publish.
 */
const T = "retag";
const ANY_HEAD = "__any__";
const PAGE = 50;

const StatCell = ({ label, value, tone = "", testId }) => (
  <div className="rounded-lg border bg-card p-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 text-xl font-semibold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const RunStatus = ({ run }) => (
  run.status === "reverted"
    ? <Badge variant="outline" className="font-normal gap-1" data-testid={`${T}-run-status-${run.run_id}`}><RotateCcw className="h-3 w-3" /> Reverted</Badge>
    : <Badge className="font-normal gap-1 bg-[hsl(var(--success))] text-white border-transparent" data-testid={`${T}-run-status-${run.run_id}`}><CheckCircle2 className="h-3 w-3" /> Applied</Badge>
);

const HeadLabel = ({ head, fallback = "—" }) => head
  ? <span>{head.tax_head_name} <span className="font-mono text-xs text-muted-foreground">{head.tax_code}</span></span>
  : <span className="text-muted-foreground">{fallback}</span>;

/** Same substring match the server applies for the property search (PIN / owner / address / mobile / house no). */
const rowMatches = (row, q) => {
  const s = (q || "").trim().toLowerCase();
  if (!s) return true;
  return [row.pin, row.owner_name, row.address, row.mobile, row.house_no].some((v) => (v || "").toLowerCase().includes(s));
};

const EMPTY_SEL = { mode: "ids", ids: [], q: "", exclude: [], total: 0 };

export default function DemandRetag() {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("muni") || "";
  const [munis, setMunis] = useState(null);
  const [opts, setOpts] = useState(null);           // heads + totals for the municipality
  const [fromHead, setFromHead] = useState(ANY_HEAD);
  const [toHead, setToHead] = useState("");
  const [includeSA, setIncludeSA] = useState(false);
  // property picker
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [props, setProps] = useState({ items: [], total: 0, offset: 0, loading: false });
  const [sel, setSel] = useState(EMPTY_SEL);          // mode "ids" (ticked ids) | mode "all" (every property matching q, minus exclude)
  // dry run
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);   // { kind: "apply" } | { kind: "revert", run }
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const reqSeq = useRef(0);

  useEffect(() => {
    api.get("/platform/municipalities").then((r) => setMunis((r.data || []).filter((m) => !m.is_deleted))).catch((e) => toast.error(errMsg(e)));
  }, []);

  // municipality options (heads with counts); default To = User Charges when it exists
  useEffect(() => {
    setOpts(null); setData(null); setError(null); setLastResult(null); setSel(EMPTY_SEL); setQ(""); setQDebounced(""); setFromHead(ANY_HEAD);
    if (!code) return;
    api.get(`/platform/municipalities/${code}/demand-retag/options`).then((r) => {
      setOpts(r.data);
      const uc = (r.data.heads || []).find((h) => (h.tax_code || "").toUpperCase() === "UC");
      setToHead(uc ? uc.id : "");
    }).catch((e) => setError(errMsg(e)));
  }, [code]);

  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 350); return () => clearTimeout(t); }, [q]);

  const loadProps = useCallback(async (offset = 0, append = false) => {
    if (!code) return;
    setProps((p) => ({ ...p, loading: true }));
    try {
      const r = await api.get(`/platform/municipalities/${code}/demand-retag/properties`, {
        params: { q: qDebounced || undefined, from_head_id: fromHead !== ANY_HEAD ? fromHead : undefined, limit: PAGE, offset },
      });
      setProps((p) => ({ items: append ? [...p.items, ...r.data.items] : r.data.items, total: r.data.total, offset, loading: false }));
    } catch (e) {
      setProps((p) => ({ ...p, loading: false }));
      toast.error(errMsg(e));
    }
  }, [code, qDebounced, fromHead]);

  useEffect(() => { loadProps(0, false); }, [loadProps]);

  const pickMuni = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("muni", v); else next.delete("muni");
    setSearchParams(next, { replace: true });
  };

  // ---- selection helpers
  const isChecked = (row) => sel.mode === "all" ? (rowMatches(row, sel.q) && !sel.exclude.includes(row.id)) : sel.ids.includes(row.id);
  const toggleRow = (row) => setSel((s) => {
    if (s.mode === "all") {
      if (!rowMatches(row, s.q)) return s;   // not part of the "all matching" set - nothing to toggle
      const ex = s.exclude.includes(row.id) ? s.exclude.filter((i) => i !== row.id) : [...s.exclude, row.id];
      return { ...s, exclude: ex };
    }
    const ids = s.ids.includes(row.id) ? s.ids.filter((i) => i !== row.id) : [...s.ids, row.id];
    return { ...s, ids };
  });
  const selectAllMatching = () => setSel({ mode: "all", ids: [], q: qDebounced, exclude: [], total: props.total });
  const clearSelection = () => setSel(EMPTY_SEL);
  const selectedCount = sel.mode === "all" ? Math.max(0, sel.total - sel.exclude.length) : sel.ids.length;
  const allHeaderState = sel.mode === "all" && sel.q === qDebounced
    ? (sel.exclude.length ? "indeterminate" : true)
    : (sel.mode === "ids" && props.items.length > 0 && props.items.every((r) => sel.ids.includes(r.id)) ? (props.items.length === props.total ? true : "indeterminate") : false);
  const selectionBody = useMemo(() => (
    sel.mode === "all" ? { mode: "all", q: sel.q || null, exclude_ids: sel.exclude } : { mode: "ids", property_ids: sel.ids }
  ), [sel]);
  const selectionLabel = sel.mode === "all"
    ? `All ${selectedCount} propert${selectedCount === 1 ? "y" : "ies"}${sel.q ? ` matching "${sel.q}"` : ""}${sel.exclude.length ? ` (except ${sel.exclude.length})` : ""}`
    : `${selectedCount} propert${selectedCount === 1 ? "y" : "ies"} selected`;

  // ---- dry run (debounced; nothing is changed by it)
  const body = useMemo(() => ({
    from_head_id: fromHead !== ANY_HEAD ? fromHead : null, to_head_id: toHead || null, selection: selectionBody, include_self_assessment: includeSA,
  }), [fromHead, toHead, selectionBody, includeSA]);

  const dryRun = useCallback(async () => {
    if (!code || !opts) return;
    const seq = ++reqSeq.current;
    setLoading(true); setError(null);
    try {
      const r = await api.post(`/platform/municipalities/${code}/demand-retag/preview`, body);
      if (seq === reqSeq.current) setData(r.data);
    } catch (e) {
      if (seq === reqSeq.current) { setData(null); setError(errMsg(e)); }
    } finally { if (seq === reqSeq.current) setLoading(false); }
  }, [code, opts, body]);

  useEffect(() => { const t = setTimeout(dryRun, 400); return () => clearTimeout(t); }, [dryRun]);

  const muniName = useMemo(() => (munis || []).find((m) => m.code === code)?.name || opts?.municipality?.name || code, [munis, code, opts]);
  const heads = opts?.heads || [];
  const fromObj = heads.find((h) => h.id === fromHead) || null;
  const toObj = heads.find((h) => h.id === toHead) || null;
  const counts = data?.counts;
  const canApprove = !!data && !data.blocked_reason && counts?.to_change > 0 && !loading;

  const openConfirm = (c) => { setTyped(""); setConfirm(c); };
  const codeOk = typed.trim().toLowerCase() === code.toLowerCase();

  const doApply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/municipalities/${code}/demand-retag/apply`, { ...body, confirm_code: typed.trim(), expected_count: counts.to_change });
      setLastResult({ kind: "apply", ...r.data });
      setData((d) => ({ ...(d || {}), ...r.data.preview, runs: r.data.runs }));
      toast.success(`${r.data.updated} demand${r.data.updated === 1 ? "" : "s"} re-tagged to ${r.data.target_head?.tax_head_name} — backup run ${r.data.run_id}`);
      setConfirm(null);
      loadProps(0, false);
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) dryRun();   // numbers moved since the dry run - refresh them
    } finally { setBusy(false); }
  };

  const doRevert = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/municipalities/${code}/demand-retag/revert`, { run_id: confirm.run.run_id, confirm_code: typed.trim() });
      setLastResult({ kind: "revert", ...r.data });
      toast.success(`Run ${r.data.run_id} reverted — ${r.data.reverted} demand${r.data.reverted === 1 ? "" : "s"} back on their previous tax head`);
      setConfirm(null);
      dryRun(); loadProps(0, false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const rules = [
    "Only the tax head changes (plus a line label when two demands of the same year end up on one head) — amounts, paid / balance figures and payment status are never touched",
    includeSA ? "Self-assessment demands are included because you switched them on" : "Demands created from citizen self-assessments stay exactly as they are unless you switch them on",
    "A property with two demands in the same year keeps both as separate lines (the second one labelled with its former head, e.g. \"User Charges (Water)\")",
    "A backup of the previous values is saved before anything changes; one click reverts the whole run",
    "Nothing runs on its own — not on publish, not on a schedule — only when you press Approve",
  ];

  return (
    <div className="space-y-5" data-testid={`${T}-page`}>
      <PageHeader title="Tax Head Re-tag" subtitle="Move demands from one tax head to another for the properties you tick — dry run first, nothing changes until you approve, every run is backed up and reversible.">
        <Tags className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      </PageHeader>

      {/* ---- 1. municipality + heads */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading">1 · Municipality and tax heads</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto_minmax(0,1fr)] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">Municipality</Label>
              <Select value={code} onValueChange={pickMuni}>
                <SelectTrigger data-testid={`${T}-muni-select`}><SelectValue placeholder={munis ? "Choose a municipality" : "Loading…"} /></SelectTrigger>
                <SelectContent>
                  {(munis || []).map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`${T}-muni-${m.code}`}>
                      <span className="inline-flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {m.name} <span className="text-muted-foreground font-mono text-xs">{m.code}</span></span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Move demands from</Label>
              <Select value={fromHead} onValueChange={(v) => setFromHead(v)} disabled={!opts}>
                <SelectTrigger data-testid={`${T}-from-select`}><SelectValue placeholder="Tax head" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_HEAD} data-testid={`${T}-from-any`}>Any tax head (all except the target)</SelectItem>
                  {heads.map((h) => (
                    <SelectItem key={h.id} value={h.id} data-testid={`${T}-from-${h.tax_code}`}>
                      {h.tax_head_name} <span className="text-muted-foreground font-mono text-xs">{h.tax_code}</span> <span className="text-muted-foreground text-xs">· {h.demands} demand{h.demands === 1 ? "" : "s"}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ArrowRight className="hidden lg:block h-5 w-5 mb-2.5 text-muted-foreground" aria-hidden="true" />
            <div className="space-y-1.5">
              <Label className="text-xs">Move demands to</Label>
              <Select value={toHead} onValueChange={setToHead} disabled={!opts}>
                <SelectTrigger data-testid={`${T}-to-select`}><SelectValue placeholder="Tax head" /></SelectTrigger>
                <SelectContent>
                  {heads.map((h) => (
                    <SelectItem key={h.id} value={h.id} data-testid={`${T}-to-${h.tax_code}`} disabled={h.id === fromHead}>
                      {h.tax_head_name} <span className="text-muted-foreground font-mono text-xs">{h.tax_code}</span> <span className="text-muted-foreground text-xs">· {h.demands} demand{h.demands === 1 ? "" : "s"}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {opts && heads.length === 0 && <p className="text-sm text-destructive flex items-center gap-1.5" data-testid={`${T}-no-heads`}><AlertCircle className="h-4 w-4" /> This municipality has no tax heads yet — create them under Master Data › Tax Heads.</p>}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Switch checked={includeSA} onCheckedChange={setIncludeSA} disabled={!opts} data-testid={`${T}-include-sa-switch`} id="retag-include-sa" />
            <div>
              <Label htmlFor="retag-include-sa" className="text-sm">Include demands created from citizen self-assessments</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Off by default — self-assessment demands stay exactly as they are. {opts ? `${heads.reduce((a, h) => a + (h.self_assessment_demands || 0), 0)} such demand(s) in this municipality.` : ""}</p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-muted-foreground" data-testid={`${T}-rules`}>
            {rules.map((r) => <li key={r} className="flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--success))]" /> {r}</li>)}
          </ul>
        </CardContent>
      </Card>

      {!code && <EmptyState title="Choose a municipality" subtitle="Then pick the tax heads and tick the properties. The dry run changes nothing." />}

      {/* ---- 2. properties */}
      {code && opts && (
        <Card className="shadow-sm" data-testid={`${T}-properties`}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base font-heading">2 · Properties</CardTitle>
                <CardDescription className="mt-1">Tick one, many or all. The re-tag applies only to ticked properties.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={selectedCount ? "default" : "outline"} className="font-normal" data-testid={`${T}-selection-badge`}>{selectionLabel}</Badge>
                {selectedCount > 0 && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={clearSelection} data-testid={`${T}-clear-selection`}><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by PIN, owner, address or mobile" className="pl-8" data-testid={`${T}-property-search`} />
              </div>
              <Button variant="outline" onClick={selectAllMatching} disabled={props.total === 0} data-testid={`${T}-select-all-button`}>
                Select all {props.total ? `(${props.total}${qDebounced ? " matching" : ""})` : ""}
              </Button>
            </div>
            <div className="rounded-md border overflow-hidden">
              <div className="flex items-center gap-3 px-3 py-2 bg-muted/40 text-xs text-muted-foreground border-b">
                <Checkbox checked={allHeaderState} onCheckedChange={(v) => (v ? selectAllMatching() : clearSelection())} disabled={props.total === 0} aria-label="Select all matching properties" data-testid={`${T}-select-all-checkbox`} />
                <span className="flex-1">{props.loading && props.items.length === 0 ? "Loading…" : `${props.total} propert${props.total === 1 ? "y" : "ies"}${qDebounced ? ` match "${qDebounced}"` : ""}`}</span>
                <span className="hidden sm:inline">Demands{fromObj ? ` · on ${fromObj.tax_head_name}` : ""}</span>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y" data-testid={`${T}-property-list`}>
                {props.items.length === 0 && !props.loading && (
                  <p className="text-sm text-muted-foreground text-center py-8" data-testid={`${T}-property-empty`}>{qDebounced ? "No property matches that search." : "No properties in this municipality yet."}</p>
                )}
                {props.items.map((row) => {
                  const checked = isChecked(row);
                  return (
                    <label key={row.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30 ${checked ? "bg-primary/5" : ""}`} data-testid={`${T}-property-row-${row.pin}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggleRow(row)} aria-label={`Select ${row.pin}`} data-testid={`${T}-property-check-${row.pin}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-tight"><span className="font-mono font-medium">{row.pin}</span> <span className="text-muted-foreground">·</span> {row.owner_name || "—"}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{[row.address, row.ward_name && `Ward ${row.ward_name}`, row.zone_name].filter(Boolean).join(" · ") || "—"}</p>
                      </div>
                      <div className="text-right text-xs tabular-nums shrink-0">
                        <span className="font-medium">{row.demands}</span>{fromObj ? <span className="text-muted-foreground"> · {row.demands_on_from_head} on {fromObj.tax_code}</span> : null}
                        {row.self_assessment_demands > 0 && <span className="block text-[10px] text-muted-foreground">{row.self_assessment_demands} self-assessment</span>}
                      </div>
                    </label>
                  );
                })}
                {props.items.length < props.total && (
                  <div className="p-2 text-center">
                    <Button size="sm" variant="ghost" onClick={() => loadProps(props.offset + PAGE, true)} disabled={props.loading} data-testid={`${T}-load-more`}>
                      {props.loading ? "Loading…" : `Show more (${props.total - props.items.length} left)`}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {code && error && (
        <Card className="shadow-sm border-destructive/40"><CardContent className="p-6 flex flex-col items-center gap-2 text-center" data-testid={`${T}-error`}>
          <AlertCircle className="h-5 w-5 text-destructive" /><p className="text-sm">{error}</p>
          <Button size="sm" variant="outline" onClick={dryRun}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
        </CardContent></Card>
      )}

      {code && opts && loading && !data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`${T}-skeleton`}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      )}

      {lastResult && (
        <Card className="shadow-sm border-[hsl(var(--success))]/40" data-testid={`${T}-last-result`}>
          <CardContent className="p-4 flex items-start gap-3 text-sm">
            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] shrink-0 mt-0.5" />
            <div>
              {lastResult.kind === "apply"
                ? <p><span className="font-semibold">{lastResult.updated}</span> demand{lastResult.updated === 1 ? "" : "s"} re-tagged to {lastResult.target_head?.tax_head_name}{lastResult.relabelled_lines ? <> ({lastResult.relabelled_lines} kept as a second labelled line)</> : null}. Backup saved as run <span className="font-mono">{lastResult.run_id}</span> — use <em>Revert</em> below to undo it.{lastResult.failed_duplicate?.length ? <span className="text-destructive"> {lastResult.failed_duplicate.length} could not be moved (unexpected duplicate) and were left unchanged.</span> : null}</p>
                : <p>Run <span className="font-mono">{lastResult.run_id}</span> reverted — <span className="font-semibold">{lastResult.reverted}</span> demand{lastResult.reverted === 1 ? "" : "s"} back on their previous tax head.</p>}
              <p className="text-xs text-muted-foreground mt-1">Amounts and payment status were not touched. The dashboard "Collection by tax head" card reflects this immediately.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- 3. dry run */}
      {data && (
        <>
          <Card className="shadow-sm" data-testid={`${T}-preview`}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base font-heading flex items-center gap-2"><ClipboardList className="h-4 w-4 text-muted-foreground" /> 3 · Dry run — {muniName}</CardTitle>
                  <CardDescription className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`${T}-preview-summary`}>
                    <span><HeadLabel head={data.source_head} fallback="Any tax head" /></span>
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    <span><HeadLabel head={data.target_head} fallback="choose a target" /></span>
                    <span>·</span><span>{data.selection?.label}</span>
                    {data.include_self_assessment && <Badge variant="outline" className="font-normal text-[10px]">self-assessment included</Badge>}
                    {loading && <RefreshCw className="h-3 w-3 animate-spin" aria-label="Refreshing" />}
                  </CardDescription>
                  <p className="text-xs text-muted-foreground mt-1">Nothing has been changed. This is what <em>would</em> happen if you approve.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={dryRun} disabled={loading} data-testid={`${T}-dry-run-button`}><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Re-run</Button>
                  <Button onClick={() => openConfirm({ kind: "apply" })} disabled={!canApprove} data-testid={`${T}-approve-button`}>
                    <Lock className="h-4 w-4 mr-2" /> Approve &amp; apply{canApprove ? ` (${counts.to_change})` : ""}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.blocked_reason && (
                <p className="text-sm text-destructive flex items-center gap-1.5" data-testid={`${T}-blocked`}><AlertCircle className="h-4 w-4" /> {data.blocked_reason}</p>
              )}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCell label="Would change" value={counts.to_change} tone={counts.to_change > 0 ? "text-primary" : "text-muted-foreground"} testId={`${T}-count-change`} />
                <StatCell label={`Already on ${data.target_head?.tax_code || "target"}`} value={counts.already_target} testId={`${T}-count-already`} />
                <StatCell label={data.include_self_assessment ? "Self-assessment (moving)" : "Self-assessment (stay)"} value={data.include_self_assessment ? counts.self_assessment_moving : counts.protected_self_assessment} testId={`${T}-count-protected`} />
                <StatCell label="Demands in ledger" value={counts.total} testId={`${T}-count-total`} />
              </div>
              {counts.to_change > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1.5" data-testid={`${T}-by-head`}>
                    <p className="text-xs font-medium text-muted-foreground">Moving from</p>
                    <ul className="space-y-1">
                      {data.by_current_head.map((h) => (
                        <li key={h.tax_code} className="flex items-center justify-between gap-3 rounded-md border px-3 py-1.5" data-testid={`${T}-by-head-${h.tax_code}`}>
                          <span className="inline-flex items-center gap-2">{h.tax_head_name} <span className="font-mono text-xs text-muted-foreground">{h.tax_code}</span> <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /> {data.target_head?.tax_head_name}</span>
                          <span className="font-semibold tabular-nums">{h.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-1.5" data-testid={`${T}-facts`}>
                    <p className="text-xs font-medium text-muted-foreground">What this touches</p>
                    <ul className="space-y-1 text-sm">
                      <li className="flex justify-between gap-3 rounded-md border px-3 py-1.5"><span>Properties affected</span><span className="font-semibold tabular-nums">{counts.properties_affected}</span></li>
                      <li className="flex justify-between gap-3 rounded-md border px-3 py-1.5"><span>Kept as a second labelled line</span><span className="font-semibold tabular-nums">{counts.relabelled_lines}</span></li>
                      <li className="flex justify-between gap-3 rounded-md border px-3 py-1.5"><span>Payment status of the moving demands</span><span className="text-xs text-muted-foreground tabular-nums text-right">{Object.entries(data.by_payment_status || {}).map(([k, v]) => `${v} ${k}`).join(" · ") || "—"}</span></li>
                      <li className="flex justify-between gap-3 rounded-md border px-3 py-1.5"><span>Amounts / payment status changed</span><span className="font-semibold text-[hsl(var(--success))]">none</span></li>
                    </ul>
                  </div>
                </div>
              ) : !data.blocked_reason && (
                <p className="text-sm text-muted-foreground" data-testid={`${T}-nothing`}>Nothing to change — no demand in your selection would move to {data.target_head?.tax_head_name}.</p>
              )}

              {data.clashing_property_years?.length > 0 && (
                <div className="space-y-1.5" data-testid={`${T}-clash`}>
                  <p className="text-xs font-medium text-muted-foreground">Properties with two demands in the same year ({counts.clashing_property_years}) — both stay as separate lines</p>
                  <div className="rounded-md border overflow-x-auto max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>PIN</TableHead><TableHead>Year</TableHead><TableHead>Heads now</TableHead><TableHead>Lines after</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {data.clashing_property_years.map((c, i) => (
                          <TableRow key={`${c.pin}-${c.fy}-${i}`} data-testid={`${T}-clash-row-${c.pin}`}>
                            <TableCell className="font-mono text-xs py-2">{c.pin}</TableCell>
                            <TableCell className="text-xs py-2">{c.fy}{c.usage ? <span className="text-muted-foreground"> ({c.usage})</span> : null}</TableCell>
                            <TableCell className="text-xs py-2">{c.heads.join(" + ")}</TableCell>
                            <TableCell className="text-xs py-2">{(c.lines_after || []).join(" + ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ---- 4. runs */}
          <Card className="shadow-sm" data-testid={`${T}-runs`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2"><History className="h-4 w-4 text-muted-foreground" /> Runs &amp; backups</CardTitle>
              <CardDescription>Every applied run keeps a backup of the previous tax heads. Revert puts exactly those demands back.</CardDescription>
            </CardHeader>
            <CardContent>
              {(data.runs || []).length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid={`${T}-runs-empty`}>No runs yet for this municipality.</p>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Run</TableHead><TableHead>When</TableHead><TableHead>By</TableHead><TableHead>Move</TableHead><TableHead className="text-right">Demands</TableHead><TableHead>Status</TableHead><TableHead className="w-28" /></TableRow></TableHeader>
                    <TableBody>
                      {data.runs.map((r) => (
                        <TableRow key={r.run_id} data-testid={`${T}-run-${r.run_id}`}>
                          <TableCell className="font-mono text-xs py-2">{r.run_id}</TableCell>
                          <TableCell className="text-xs py-2">{r.at ? fmtDateTime(r.at) : "—"}{r.status === "reverted" && r.reverted_at ? <span className="block text-muted-foreground">reverted {fmtDateTime(r.reverted_at)}</span> : null}</TableCell>
                          <TableCell className="text-xs py-2">{r.actor?.name || "—"}{r.mode === "cli" ? <span className="text-muted-foreground"> (command line)</span> : null}</TableCell>
                          <TableCell className="text-xs py-2">
                            <span className="inline-flex items-center gap-1">{r.source_head?.tax_code || "Any"} <ArrowRight className="h-3 w-3 text-muted-foreground" /> {r.target_head?.tax_code || "UC"}</span>
                            <span className="block text-muted-foreground">{r.selection?.label || "All properties"}{r.include_self_assessment ? " · incl. self-assessment" : ""}</span>
                          </TableCell>
                          <TableCell className="text-xs py-2 text-right tabular-nums">{r.changed}{r.relabelled_lines ? <span className="text-muted-foreground"> ({r.relabelled_lines} labelled)</span> : null}</TableCell>
                          <TableCell className="py-2"><RunStatus run={r} /></TableCell>
                          <TableCell className="py-2 text-right">
                            {r.can_revert && (
                              <Button size="sm" variant="outline" onClick={() => openConfirm({ kind: "revert", run: r })} data-testid={`${T}-revert-${r.run_id}`}>
                                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Revert
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o && !busy) setConfirm(null); }}>
        <AlertDialogContent data-testid={`${T}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "revert" ? `Revert run ${confirm.run.run_id}?` : `Approve re-tag for ${muniName}?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {confirm?.kind === "revert" ? (
                  <p><span className="font-semibold text-foreground">{confirm.run.still_applied}</span> demand{confirm.run.still_applied === 1 ? "" : "s"} go back to the tax head and line label they had before this run. Amounts and payment status are untouched.</p>
                ) : (
                  <p>A backup of the current values is saved first, then <span className="font-semibold text-foreground">{counts?.to_change}</span> demand{counts?.to_change === 1 ? "" : "s"} on {counts?.properties_affected} propert{counts?.properties_affected === 1 ? "y" : "ies"} move from <span className="font-semibold text-foreground">{fromObj?.tax_head_name || "their current tax head"}</span> to <span className="font-semibold text-foreground">{toObj?.tax_head_name}</span> ({data?.selection?.label}). {includeSA ? "Self-assessment demands are included." : "Self-assessment demands stay as they are."} Amounts and payment status are untouched. You can revert the whole run afterwards.</p>
                )}
                <p>Type the municipality code <span className="font-mono font-semibold text-foreground">{code}</span> to confirm.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={code} className="font-mono" autoFocus data-testid={`${T}-confirm-input`}
            onKeyDown={(e) => { if (e.key === "Enter" && codeOk && !busy) (confirm?.kind === "revert" ? doRevert() : doApply()); }} />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`${T}-confirm-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!codeOk || busy} onClick={(e) => { e.preventDefault(); (confirm?.kind === "revert" ? doRevert() : doApply()); }} data-testid={`${T}-confirm-submit`}>
              {busy ? "Working…" : confirm?.kind === "revert" ? "Revert now" : "Approve & apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
